import { PersonaSize } from '@fluentui/react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { IconName } from '../../models/components.models';
import { useClassNames, useMediaQuery } from '../../utils/hooks';
import { Button, IButtonProps } from '../Button/Button';
import { buttonStylesIcon, buttonStylesMenu, buttonStylesMenuIcon } from '../Button/Button.styles';
import { Image } from '../Image/Image';
import { imageStylesLogo } from '../Image/Image.styles';
import { IPersonaProps, Persona } from '../Persona/Persona';
import { personaStylesMenuButton } from '../Persona/Persona.styles';
import { appBarNavigationStylesDefault } from './AppBarNavigation.styles';
import { useAppNavigationContext } from './AppNavigationContext';
import { narrowSectionWidth, wideSectionWidth } from './components/NavigationSection/constants';
import { NavigationSection } from './components/NavigationSection/NavigationSection';
import type { AppBarNavigationClassNames, AppBarNavigationProps, AppBarNavigationStyles } from './types';

export enum SidebarState {
  Compact = 0,
  ExpandedHover = 1,
  ExpandedAlways = 2,
}

export const AppBarNavigation = (props: AppBarNavigationProps) => {
  const {
    children,
    className,
    navigationConfig,
    userMenuItems,
    menuButtonProps = {},
    menuIconButtonProps = {},
    personaProps,
    logoImageProps,
    title,
    styles,
    onLogoClick,
    onTitleClick,
    hideNavigation,
    headerContent,
    logoElement,
  } = props;
  if (process.env.NODE_ENV === 'development' && logoImageProps) {
    console.warn(
      '`logoImageProps` is deprecated and will be removed in the next major release. Please use `logoElement` instead.'
    );
  }

  const { isMobile } = useMediaQuery();
  const [sidebarState, setSidebarState] = useState(isMobile ? SidebarState.Compact : SidebarState.ExpandedAlways);
  const location = useLocation();
  useEffect(() => {
    if (isMobile) setSidebarState(SidebarState.Compact);
  }, [location.pathname, isMobile]);

  const {
    styles: mibStyles,
    iconName: mibIconName = IconName.GlobalNavButton,
    title: mibTitle = 'Menu',
    onClick: mibOnClick = () => {
      setSidebarState((prevState) => {
        if (prevState === SidebarState.Compact) return SidebarState.ExpandedAlways;
        return SidebarState.Compact;
      });
    },
  } = menuIconButtonProps;

  const [twoLevelNavigation, setTwoLevelNavigation] = useState<boolean>(false);
  const setActiveNavigationKeys = useCallback((activeKeys: string[]) => {
    // Defer updates because some navigation components call this during render.
    Promise.resolve().then(() => {
      setTwoLevelNavigation(activeKeys.length > 1);
    });
  }, []);

  const classNames = useClassNames<AppBarNavigationStyles, AppBarNavigationClassNames>(
    'AppBarNavigation',
    appBarNavigationStylesDefault,
    styles,
    className,
    sidebarState === SidebarState.Compact && 'main-nav-compact',
    Boolean(sidebarState & SidebarState.ExpandedHover) && 'main-nav-expand-soft',
    Boolean(sidebarState & SidebarState.ExpandedAlways) && 'main-nav-expand-hard',
    twoLevelNavigation && 'main-nav-two-plus-levels'
  );

  let _personaProps: IPersonaProps;
  if (personaProps) {
    const { size = PersonaSize.size40, styles = personaStylesMenuButton } = personaProps;
    _personaProps = { ...personaProps, styles, size };
  }

  const {
    styles: mbStyles,
    menuItems: mbMenuItems = userMenuItems,
    onRenderText: mbLabelRenderer = () => <Persona key="app-bar-navigation-user-menu-persona" {..._personaProps} />,
  } = menuButtonProps;
  const mibProps: IButtonProps = {
    ...menuIconButtonProps,
    styles: mibStyles || [buttonStylesIcon, buttonStylesMenuIcon],
    iconName: mibIconName,
    title: mibTitle,
    onClick: mibOnClick,
  };
  const mbProps: IButtonProps = {
    ...menuButtonProps,
    styles: mbStyles || [buttonStylesMenu],
    menuItems: mbMenuItems,
    onRenderText: mbLabelRenderer,
  };

  const { setNavigationSize } = useAppNavigationContext();
  useEffect(() => {
    if (!isMobile) {
      if (hideNavigation) {
        setNavigationSize(0);
        return;
      }

      if (sidebarState === SidebarState.Compact) {
        setNavigationSize(narrowSectionWidth);
      } else {
        setNavigationSize(twoLevelNavigation ? narrowSectionWidth + wideSectionWidth : wideSectionWidth);
      }
      // useEffect is not supposed to react to the ref change, but for some reason, it works.
      // Fixes a but with the navigation size that is set when the page loads.
      // TODO: find a correct solution
    } else {
      setNavigationSize(0);
    }
  }, [isMobile, sidebarState, setNavigationSize, twoLevelNavigation, hideNavigation]);

  return (
    <div className={classNames.root} data-test={props['data-test'] || 'app-bar-navigation'}>
      {!hideNavigation && (
        <NavigationSection
          config={{ key: `root`, section: navigationConfig }}
          softExpand={Boolean(sidebarState & SidebarState.ExpandedHover)}
          setActiveNavigationKeys={setActiveNavigationKeys}
          setSoftExpand={(hovered) => {
            setSidebarState((oldState) => {
              return hovered ? oldState | SidebarState.ExpandedHover : oldState & ~SidebarState.ExpandedHover;
            });
          }}
        />
      )}

      {isMobile && sidebarState !== SidebarState.Compact && (
        <div className={classNames.menuBackCover} onClick={() => setSidebarState(SidebarState.Compact)} />
      )}

      <div className={classNames.column}>
        {!hideNavigation && (
          <div className={classNames.menuIcon}>
            <Button {...mibProps} />
          </div>
        )}

        {/* Replaces the logoImageProps prop. */}
        {logoElement && !logoImageProps && (
          <div className={classNames.logo} onClick={onLogoClick}>
            {logoElement}
          </div>
        )}

        {/* Should be replaced with the logoElement prop. Going to be deprecated! */}
        {logoImageProps && !logoElement && (
          <div className={classNames.logo} onClick={onLogoClick}>
            <Image {...logoImageProps} styles={logoImageProps?.styles || imageStylesLogo} />
          </div>
        )}

        {title && (
          <div className={classNames.title} onClick={onTitleClick}>
            {title}
          </div>
        )}

        {headerContent && <div className={classNames.headerContent}>{headerContent}</div>}
      </div>

      <div className={classNames.column}>
        {children}
        {personaProps && (
          <div className={classNames.userMenu}>
            <Button data-test="app-bar-user-menu-button" {...mbProps} />
          </div>
        )}
      </div>
    </div>
  );
};
